import { useState } from 'react';
import { motion } from 'framer-motion';
import MarketingLayout from '../components/MarketingLayout';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Phone, MapPin, Send, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '', request_type: 'contact' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <MarketingLayout>
      <SEO
        title="Contact"
        description="Get in touch with the PAFHub team. We're here to help with H-1B compliance questions, product demos, and enterprise inquiries."
        path="/contact"
        keywords="contact PAFHub, H-1B compliance support, immigration software demo"
      />
      {/* SEO-only H1 (visually hidden, fully accessible to search engines & screen readers) */}
      <h1 className="sr-only">PAFHub — Simplify H-1B, H-1B1 & E-3 Public Access Files(PAF) Compliance</h1>

      {/* Hero */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-br from-indigo-50/80 via-white to-sky-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-3">Contact</p>
            <h2 className="text-4xl lg:text-6xl font-bold text-[#0F172A] tracking-tight mb-6">Get in touch</h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
              Have questions about PAFHub? Want a demo? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
                <h2 className="text-2xl font-bold text-[#0F172A] tracking-tight mb-6">Let's talk compliance</h2>
                <p className="text-[#64748B] leading-relaxed mb-10">
                  Whether you're looking for a product demo, have pricing questions, or need technical support — our team is here to help.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-indigo-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[#0F172A]">Email</p>
                      <a href="mailto:hello@pafhub.com" className="text-sm text-[#64748B] hover:text-indigo-600 transition-colors">hello@pafhub.com</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-indigo-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[#0F172A]">Phone</p>
                      <a href="tel:+18173827102" className="text-sm text-[#64748B] hover:text-indigo-600 transition-colors">817-382-7102</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-indigo-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[#0F172A]">Office</p>
                      <p className="text-sm text-[#64748B]">Southlake, Texas</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }}>
                {sent ? (
                  <div className="bg-[#F8FAFF] rounded-2xl border border-indigo-100/50 p-12 text-center" data-testid="contact-success">
                    <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-6" strokeWidth={1.5} />
                    <h3 className="text-2xl font-bold text-[#0F172A] mb-3">Message sent!</h3>
                    <p className="text-[#64748B]">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                    <Button onClick={() => { setSent(false); setForm({ name: '', email: '', company: '', message: '', request_type: 'contact' }); }}
                      className="mt-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
                    >
                      Send another message
                    </Button>
                  </div>
                ) : (
                  <div className="bg-[#F8FAFF] rounded-2xl border border-indigo-100/50 p-8 lg:p-10">
                    <div className="flex gap-3 mb-8">
                      <button
                        onClick={() => setForm({ ...form, request_type: 'contact' })}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${form.request_type === 'contact' ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-[#64748B] border border-indigo-100'}`}
                        data-testid="tab-contact"
                      >
                        General Inquiry
                      </button>
                      <button
                        onClick={() => setForm({ ...form, request_type: 'demo' })}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${form.request_type === 'demo' ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-[#64748B] border border-indigo-100'}`}
                        data-testid="tab-demo"
                      >
                        Request Demo
                      </button>
                    </div>

                    {error && <div className="mb-6 p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl">{error}</div>}

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Full Name *</Label>
                          <Input
                            data-testid="contact-name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            required
                            placeholder="John Doe"
                            className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Email *</Label>
                          <Input
                            data-testid="contact-email"
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            required
                            placeholder="john@company.com"
                            className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Company</Label>
                        <Input
                          data-testid="contact-company"
                          value={form.company}
                          onChange={(e) => setForm({ ...form, company: e.target.value })}
                          placeholder="Your company name"
                          className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                          {form.request_type === 'demo' ? 'What would you like to see in the demo?' : 'Message'} *
                        </Label>
                        <textarea
                          data-testid="contact-message"
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                          required
                          rows={5}
                          placeholder={form.request_type === 'demo' ? 'Tell us about your H-1B, H-1B1, and E-3 program and what you would like to see...' : 'How can we help you?'}
                          className="w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all resize-none"
                        />
                      </div>
                      <Button
                        data-testid="contact-submit"
                        type="submit"
                        disabled={sending}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-12 font-semibold shadow-md hover:shadow-lg hover:shadow-indigo-500/25 transition-all"
                      >
                        {sending ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <span className="flex items-center gap-2">
                            <Send className="w-4 h-4" />
                            {form.request_type === 'demo' ? 'Request Demo' : 'Send Message'}
                          </span>
                        )}
                      </Button>
                    </form>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
